import React, { useState, useEffect, useCallback } from "react";
import apiPath from "../../constants/apiPath";

const BASE = apiPath.baseURL; // "https://gym.fusionhub.studio/api/v1/"

const COLORS = {
  bg: "#0d0d1a",
  surface: "#13132b",
  border: "rgba(255,255,255,0.08)",
  yellow: "#FCDC08",
  blue: "#004aad",
  text: "#fff",
  muted: "rgba(255,255,255,0.5)",
  ok: "#27ae60",
  warn: "#e67e22",
  err: "#e74c3c",
};

const STATUS_COLOR = { ok: COLORS.ok, warning: COLORS.warn, error: COLORS.err };
const STATUS_LABEL = { ok: "OK", warning: "NO BLOCKS TODAY", error: "NO PLAN" };
const STATUS_BG = {
  ok: "rgba(39,174,96,0.15)",
  warning: "rgba(230,126,34,0.15)",
  error: "rgba(231,76,60,0.15)",
};

// ─── Login ────────────────────────────────────────────────────────────────────
function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const res = await fetch(`${BASE}${apiPath.login}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      const user = data?.data?.user || data?.user;
      const token = user?.token || user?.access_token;
      if (!token) { setErr(data?.message || "Invalid credentials"); setLoading(false); return; }
      const role = (user?.role || "").toLowerCase();
      if (!["admin", "ops"].includes(role) && !user?.is_superuser) {
        setErr("Access denied — admin only"); setLoading(false); return;
      }
      localStorage.setItem("fh_admin_token", token);
      onLogin(token);
    } catch { setErr("Connection error. Please try again."); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif" }}>
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 16, padding: "40px 48px", width: 360 }}>
        <div style={{ marginBottom: 32, textAlign: "center" }}>
          <span style={{ fontSize: 26, fontWeight: 900, color: COLORS.yellow }}>fusion</span>
          <span style={{ fontSize: 26, fontWeight: 900, color: COLORS.text }}>hub</span>
          <span style={{ display: "inline-block", background: COLORS.yellow, color: COLORS.bg, fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 4, marginLeft: 6, verticalAlign: "middle", letterSpacing: 1 }}>GYM TV</span>
          <div style={{ color: COLORS.muted, fontSize: 13, marginTop: 8 }}>Admin Diagnostics</div>
        </div>
        <form onSubmit={submit}>
          <input
            type="email" placeholder="Admin email" value={email}
            onChange={e => setEmail(e.target.value)} required
            style={inputStyle}
          />
          <input
            type="password" placeholder="Password" value={password}
            onChange={e => setPassword(e.target.value)} required
            style={{ ...inputStyle, marginTop: 12 }}
          />
          {err && <div style={{ color: COLORS.err, fontSize: 13, marginTop: 8 }}>{err}</div>}
          <button type="submit" disabled={loading} style={btnStyle}>
            {loading ? "Signing in…" : "Sign In →"}
          </button>
        </form>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%", background: "rgba(255,255,255,0.06)", border: `1px solid ${COLORS.border}`,
  borderRadius: 8, padding: "10px 14px", color: "#fff", fontSize: 14, outline: "none",
  boxSizing: "border-box",
};
const btnStyle = {
  marginTop: 20, width: "100%", background: COLORS.yellow, color: COLORS.bg,
  border: "none", borderRadius: 8, padding: "12px 0", fontSize: 15, fontWeight: 800,
  cursor: "pointer",
};

// ─── Week cell ─────────────────────────────────────────────────────────────────
function WeekCell({ entry, dateStr }) {
  const isToday = entry.is_today;
  const hasBlocks = entry.block_count > 0;
  return (
    <div title={entry.blocks.join(", ") || "No blocks"} style={{
      background: hasBlocks
        ? isToday ? "rgba(252,220,8,0.2)" : "rgba(39,174,96,0.12)"
        : "rgba(255,255,255,0.03)",
      border: isToday ? `1px solid ${COLORS.yellow}` : `1px solid ${COLORS.border}`,
      borderRadius: 6, padding: "4px 6px", textAlign: "center", minWidth: 52,
    }}>
      <div style={{ fontSize: 10, color: COLORS.muted, marginBottom: 2 }}>{entry.label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: hasBlocks ? (isToday ? COLORS.yellow : COLORS.ok) : COLORS.err }}>
        {hasBlocks ? entry.block_count : "–"}
      </div>
      {hasBlocks && <div style={{ fontSize: 9, color: COLORS.muted }}>{entry.task_count}t</div>}
    </div>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────────
function GymRow({ gym }) {
  const [open, setOpen] = useState(false);
  const sc = STATUS_COLOR[gym.status];
  const sb = STATUS_BG[gym.status];

  return (
    <>
      <tr
        onClick={() => setOpen(o => !o)}
        style={{ cursor: "pointer", borderBottom: `1px solid ${COLORS.border}` }}
      >
        {/* Status dot */}
        <td style={{ padding: "14px 16px", width: 12 }}>
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: sc }} />
        </td>
        {/* Gym name */}
        <td style={{ padding: "14px 16px" }}>
          <div style={{ fontWeight: 700, color: COLORS.text, fontSize: 14 }}>
            {gym.franchise_id || gym.name}
          </div>
          <div style={{ fontSize: 11, color: COLORS.muted }}>{gym.email}</div>
        </td>
        {/* Subscription */}
        <td style={{ padding: "14px 16px" }}>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
            background: gym.subscription_status === "Active" ? "rgba(39,174,96,0.15)"
              : gym.subscription_status === "Trial" ? "rgba(252,220,8,0.15)"
              : "rgba(231,76,60,0.15)",
            color: gym.subscription_status === "Active" ? COLORS.ok
              : gym.subscription_status === "Trial" ? COLORS.yellow
              : COLORS.err,
          }}>
            {gym.subscription_status}
          </span>
        </td>
        {/* Timezone */}
        <td style={{ padding: "14px 16px", color: COLORS.muted, fontSize: 12 }}>
          {gym.timezone.replace("Australia/", "").replace("Pacific/", "")}
          <div style={{ fontSize: 10 }}>{gym.today_label}</div>
        </td>
        {/* Today blocks */}
        <td style={{ padding: "14px 16px", textAlign: "center" }}>
          <span style={{ fontSize: 20, fontWeight: 900, color: gym.today_block_count > 0 ? COLORS.ok : COLORS.err }}>
            {gym.today_block_count}
          </span>
          <div style={{ fontSize: 10, color: COLORS.muted }}>{gym.today_task_count} tasks</div>
        </td>
        {/* Status badge */}
        <td style={{ padding: "14px 16px" }}>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 4, background: sb, color: sc }}>
            {STATUS_LABEL[gym.status]}
          </span>
        </td>
        {/* Chevron */}
        <td style={{ padding: "14px 16px", color: COLORS.muted, textAlign: "right" }}>
          {open ? "▲" : "▼"}
        </td>
      </tr>
      {open && (
        <tr style={{ background: "rgba(255,255,255,0.02)" }}>
          <td colSpan={7} style={{ padding: "16px 24px 20px" }}>
            {/* Issues */}
            {gym.issues.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {gym.issues.map((iss, i) => (
                  <div key={i} style={{ color: COLORS.warn, fontSize: 12, marginBottom: 4 }}>
                    ⚠ {iss}
                  </div>
                ))}
              </div>
            )}
            {/* Today's blocks detail */}
            {gym.today_blocks.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 8, letterSpacing: 1, textTransform: "uppercase" }}>Today's blocks</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {gym.today_blocks.map(b => (
                    <div key={b.key} style={{
                      background: "rgba(252,220,8,0.1)", border: `1px solid rgba(252,220,8,0.25)`,
                      borderRadius: 6, padding: "6px 12px",
                    }}>
                      <div style={{ color: COLORS.yellow, fontWeight: 700, fontSize: 13 }}>{b.title}</div>
                      <div style={{ color: COLORS.muted, fontSize: 11 }}>{b.task_count} tasks</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Week grid */}
            <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 8, letterSpacing: 1, textTransform: "uppercase" }}>Week view</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {Object.entries(gym.week_summary).sort(([a], [b]) => a.localeCompare(b)).map(([ds, entry]) => (
                <WeekCell key={ds} entry={entry} dateStr={ds} />
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AdminGymTV() {
  const [token, setToken] = useState(() => localStorage.getItem("fh_admin_token") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | ok | warning | error
  const [lastFetch, setLastFetch] = useState(null);

  const fetchData = useCallback(async (tok) => {
    if (!tok) return;
    setLoading(true); setErr("");
    try {
      const res = await fetch(`${BASE}admin/gymtv_diagnostics`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (res.status === 401 || res.status === 403) {
        localStorage.removeItem("fh_admin_token");
        setToken("");
        setLoading(false);
        return;
      }
      const json = await res.json();
      if (!json.status) { setErr(json.error || "Failed to load"); }
      else { setData(json); setLastFetch(new Date()); }
    } catch (e) { setErr("Network error: " + e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { if (token) fetchData(token); }, [token, fetchData]);

  if (!token) return <AdminLogin onLogin={tok => { setToken(tok); }} />;

  const gyms = data?.data || [];
  const summary = data?.summary || {};

  const filtered = gyms.filter(g => {
    const matchSearch = !search
      || g.franchise_id?.toLowerCase().includes(search.toLowerCase())
      || g.name?.toLowerCase().includes(search.toLowerCase())
      || g.email?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === "all" || g.status === filter;
    return matchSearch && matchFilter;
  });

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bg, fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif", color: COLORS.text }}>
      {/* Header */}
      <div style={{ background: COLORS.surface, borderBottom: `1px solid ${COLORS.border}`, padding: "16px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div>
            <span style={{ fontSize: 22, fontWeight: 900, color: COLORS.yellow }}>fusion</span>
            <span style={{ fontSize: 22, fontWeight: 900, color: COLORS.text }}>hub</span>
            <span style={{ display: "inline-block", background: COLORS.yellow, color: COLORS.bg, fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 4, marginLeft: 6, verticalAlign: "middle", letterSpacing: 1 }}>GYM TV</span>
          </div>
          <div style={{ color: COLORS.muted, fontSize: 14, borderLeft: `1px solid ${COLORS.border}`, paddingLeft: 16 }}>
            Admin Diagnostics
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {lastFetch && <span style={{ color: COLORS.muted, fontSize: 12 }}>Updated {lastFetch.toLocaleTimeString()}</span>}
          <button onClick={() => fetchData(token)} disabled={loading} style={{
            background: "rgba(255,255,255,0.06)", border: `1px solid ${COLORS.border}`,
            color: COLORS.text, borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 13,
          }}>
            {loading ? "Refreshing…" : "↻ Refresh"}
          </button>
          <button onClick={() => { localStorage.removeItem("fh_admin_token"); setToken(""); setData(null); }} style={{
            background: "transparent", border: `1px solid ${COLORS.border}`,
            color: COLORS.muted, borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 13,
          }}>
            Sign out
          </button>
        </div>
      </div>

      <div style={{ padding: "28px 32px" }}>
        {/* Summary cards */}
        {data && (
          <div style={{ display: "flex", gap: 16, marginBottom: 28 }}>
            {[
              { label: "Total Studios", value: summary.total, color: COLORS.text },
              { label: "All Good", value: summary.ok, color: COLORS.ok },
              { label: "No Blocks Today", value: summary.warning, color: COLORS.warn },
              { label: "No Plan", value: summary.error, color: COLORS.err },
            ].map(c => (
              <div key={c.label} style={{
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                borderRadius: 12, padding: "16px 24px", flex: 1,
              }}>
                <div style={{ color: COLORS.muted, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>{c.label}</div>
                <div style={{ fontSize: 32, fontWeight: 900, color: c.color }}>{c.value ?? "—"}</div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center" }}>
          <input
            placeholder="Search gym name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle, width: 280 }}
          />
          {["all", "ok", "warning", "error"].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{
              background: filter === f ? COLORS.yellow : "rgba(255,255,255,0.06)",
              color: filter === f ? COLORS.bg : COLORS.text,
              border: `1px solid ${filter === f ? COLORS.yellow : COLORS.border}`,
              borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 12, fontWeight: 700,
              textTransform: "uppercase",
            }}>
              {f === "all" ? "All" : STATUS_LABEL[f] || f}
            </button>
          ))}
        </div>

        {/* Error */}
        {err && <div style={{ color: COLORS.err, marginBottom: 16, fontSize: 13 }}>⚠ {err}</div>}

        {/* Loading */}
        {loading && !data && (
          <div style={{ color: COLORS.muted, padding: 40, textAlign: "center" }}>Loading diagnostics…</div>
        )}

        {/* Table */}
        {filtered.length > 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "rgba(255,255,255,0.04)", borderBottom: `1px solid ${COLORS.border}` }}>
                  {["", "Studio", "Plan", "Timezone / Today", "Blocks Today", "Status", ""].map((h, i) => (
                    <th key={i} style={{ padding: "10px 16px", color: COLORS.muted, fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", textAlign: "left" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(gym => <GymRow key={gym.id} gym={gym} />)}
              </tbody>
            </table>
          </div>
        )}

        {filtered.length === 0 && data && !loading && (
          <div style={{ color: COLORS.muted, padding: 40, textAlign: "center" }}>
            {search || filter !== "all" ? "No gyms match your filters." : "No gyms found."}
          </div>
        )}
      </div>
    </div>
  );
}
